import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiUserPlus } from 'react-icons/fi';
import { Link, useParams, useRouteMatch } from 'react-router-dom';

import { DeprecatedQuickRepliesIcon } from '@mssgme/icons';
import { IconButton, ElevatedButton, useEnhancedFormik, useEventEmitter, useAutoSubmit } from '@mssgme/ui';

import MessageInput from './MessageInput';
import AttachFile from './AttachFile';
import useMessagesFooter from './useMessagesFooter';

import styles from './MessagesFooter.scss';

PureMessagesFooter.propTypes = {
    isAssigned: PropTypes.bool,
    onSendMessage: PropTypes.func,
    onSendAttachment: PropTypes.func,
    onAssign: PropTypes.func,
    isFetching: PropTypes.bool,
};

PureMessagesFooter.defaultProps = {
    isAssigned: false,
    message: '',
};

export function PureMessagesFooter({ isAssigned, isFetching, onSendMessage, onSendAttachment, onAssign }) {
    const { t } = useTranslation('inbox');
    const { url } = useRouteMatch();

    const attachFormik = useEnhancedFormik({
        initialValues: { attachment: null },
        onSubmit: onSendAttachment,
        resetOnSuccess: true,
    });
    const messageFormik = useEnhancedFormik({
        initialValues: { message: '' },
        onSubmit: onSendMessage,
        resetOnSuccess: true,
    });

    useAutoSubmit(attachFormik, 200);

    const ee = useEventEmitter();

    useEffect(() => {
        const handleReplySelect = (value) => messageFormik.setFieldValue('message', value);

        ee.on('inbox:replySelect', handleReplySelect);
        return () => ee.removeListener('inbox:replySelect', handleReplySelect);
    }, [messageFormik.setFieldValue]);

    useEffect(() => {
        ee.emit('inbox:messageChange', messageFormik.values.message);
    }, [messageFormik.values.message]);

    return (
        <footer className={styles.root}>
            {isAssigned ? (
                <div className={styles.send}>
                    <form onSubmit={attachFormik.handleSubmit}>
                        <AttachFile
                            isFetching={isFetching}
                            onChange={(e) => attachFormik.setFieldValue('attachment', e.target.files[0])}
                            error={attachFormik.errors.attachment}
                        />
                    </form>
                    <form className={styles.input} onSubmit={messageFormik.handleSubmit}>
                        <MessageInput
                            placeholder={t('messagesFooter.placeholder')}
                            onChange={messageFormik.handleChange}
                            value={messageFormik.values.message}
                            onEnterPress={() => messageFormik.submitForm()}
                            error={messageFormik.errors.message}
                        >
                            <IconButton as={Link} to={`${url}/replies`} className={styles.quickReplyBtn}>
                                <DeprecatedQuickRepliesIcon title={t('SendMessage.quickRepliesTip')} />
                            </IconButton>
                        </MessageInput>
                    </form>
                </div>
            ) : (
                <div className={styles.assign}>
                    <ElevatedButton onClick={onAssign} startIcon={<FiUserPlus size={24} />}>
                        {t('messagesFooter.button.assign')}
                    </ElevatedButton>
                </div>
            )}
        </footer>
    );
}

export default function MessagesFooter(props) {
    const { contactId } = useParams();
    const state = useMessagesFooter(contactId);

    return <PureMessagesFooter {...state} {...props} />;
}
